import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, CalendarClock, Copy, CreditCard, Crown, Download, Gift, KeyRound, Landmark, Languages, LoaderCircle, Mail, MapPin, Pencil, Phone, Share2, Target, Trash2, UserRound, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TARGETS } from "@/lib/langs";
import { RenewalChecklistButton } from "@/components/dashboard/RenewalChecklistButton";
import { EASE } from "@/components/landing/Reveal";

const FREE_FEATURES = [
  "Feuille de route personnalisée",
  "Checklist de documents",
  "Suivi des échéances",
  "Coach & Assistant IA : 10 messages/jour",
  "Quiz d'entraînement illimités",
  "1 examen blanc par jour",
];

const PREMIUM_FEATURES = [
  "Coach civique IA illimité",
  "Assistant démarches IA illimité",
  "Examens blancs illimités & corrections détaillées",
  "Formations & ressources complètes",
  "Rappels d'échéances avancés",
  "Support prioritaire",
];

function PasswordChangeSection() {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) {
      toast.error("La confirmation ne correspond pas au nouveau mot de passe.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success(data.message);
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de modifier le mot de passe.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1, ease: EASE }}
      className="card-integra p-6" data-testid="settings-password-section"
    >
      <h2 className="flex items-center gap-2 text-base font-bold text-slate-900"><KeyRound className="h-4 w-4 text-bleu" /> Sécurité — Mot de passe</h2>
      <p className="mt-2 text-xs leading-relaxed text-slate-500">
        Modifiez votre mot de passe. Par sécurité, toutes vos autres sessions (autres navigateurs, téléphone…) seront immédiatement déconnectées.
      </p>
      <form onSubmit={submit} className="mt-4 space-y-3">
        <input type="password" required value={form.current} onChange={(e) => setForm((f) => ({ ...f, current: e.target.value }))}
          placeholder="Mot de passe actuel" autoComplete="current-password"
          className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-bleu focus:outline-none" data-testid="password-current-input" />
        <div className="grid gap-3 sm:grid-cols-2">
          <input type="password" required minLength={8} maxLength={128} value={form.next} onChange={(e) => setForm((f) => ({ ...f, next: e.target.value }))}
            placeholder="Nouveau mot de passe (8 caractères min.)" autoComplete="new-password"
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-bleu focus:outline-none" data-testid="password-new-input" />
          <input type="password" required minLength={8} maxLength={128} value={form.confirm} onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
            placeholder="Confirmer le nouveau mot de passe" autoComplete="new-password"
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-bleu focus:outline-none" data-testid="password-confirm-input" />
        </div>
        <button type="submit" disabled={saving || !form.current || !form.next || !form.confirm}
          className="btn-secondary !px-6 !py-2.5 !text-xs disabled:opacity-50" data-testid="password-change-submit">
          {saving ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <><KeyRound className="h-3.5 w-3.5" /> Changer mon mot de passe</>}
        </button>
      </form>
    </motion.section>
  );
}

const DELETE_REASONS = [
  "Mes démarches sont terminées",
  "Je n'utilise plus le service",
  "Le service est trop cher",
  "Protection de mes données personnelles",
  "Autre",
];

export default function Parametres() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [coords, setCoords] = useState({ address: "", postal_code: "", phone: "", city: "" });
  const [cities, setCities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [delForm, setDelForm] = useState({ reason: DELETE_REASONS[0], detail: "", password: "" });
  const [deleting, setDeleting] = useState(false);
  const premium = user?.plan === "premium";

  const saveLang = async (value) => {
    try {
      const { data } = await api.patch("/account/language", { lang: value });
      setUser(data);
      toast.success("Langue de traduction enregistrée.");
    } catch {
      toast.error("Impossible d'enregistrer la langue.");
    }
  };

  useEffect(() => {
    if (!editing || !/^\d{5}$/.test(coords.postal_code)) { setCities([]); return; }
    let stale = false;
    api.get(`/geo/postal/${coords.postal_code}`)
      .then(({ data }) => {
        if (stale) return;
        const list = data.cities || [];
        setCities(list);
        setCoords((c) => ({ ...c, city: list.length === 1 ? list[0] : (list.includes(c.city) ? c.city : "") }));
      })
      .catch(() => { if (!stale) setCities([]); });
    return () => { stale = true; };
  }, [coords.postal_code, editing]);

  const [titre, setTitre] = useState({ type: user?.titre_type || "", expiry: user?.titre_expiry || "" });
  const [savingTitre, setSavingTitre] = useState(false);
  const expiryDays = user?.titre_expiry ? Math.ceil((new Date(user.titre_expiry) - Date.now()) / 86400000) : null;

  const [referral, setReferral] = useState(null);
  useEffect(() => {
    api.get("/referral").then((r) => setReferral(r.data)).catch(() => {});
  }, []);

  const copyReferral = () => {
    navigator.clipboard.writeText(referral.link);
    toast.success("Lien de parrainage copié. Partagez-le avec vos proches !");
  };

  const shareReferral = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "INTEGRA FRANCE",
          text: "Je t'invite sur INTEGRA FRANCE pour t'accompagner dans tes démarches et ton intégration en France.",
          url: referral.link,
        });
      } catch { /* partage annulé */ }
    } else copyReferral();
  };

  const saveTitre = async () => {
    if (savingTitre || titre.type.trim().length < 2) return;
    setSavingTitre(true);
    try {
      const { data } = await api.patch("/account/titre", { titre_type: titre.type.trim(), titre_expiry: titre.expiry || null });
      setUser(data.user);
      toast.success(data.renewal_deadline
        ? `Titre enregistré — rappel de renouvellement ajouté à vos échéances pour le ${new Date(data.renewal_deadline.date).toLocaleDateString("fr-FR")}.`
        : "Titre de séjour enregistré.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'enregistrer votre titre.");
    } finally {
      setSavingTitre(false);
    }
  };

  const exportData = async () => {
    try {
      const { data } = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `integra-france-mes-donnees-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Vos données ont été téléchargées.");
    } catch {
      toast.error("Impossible d'exporter vos données.");
    }
  };

  const deleteAccount = async (e) => {
    e.preventDefault();
    setDeleting(true);
    try {
      const reason = delForm.detail.trim() ? `${delForm.reason} — ${delForm.detail.trim()}` : delForm.reason;
      await api.post("/account/delete", { password: delForm.password, reason });
      setUser(null);
      toast.success("Votre compte et toutes vos données ont été supprimés. Au revoir !");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.status === 401 ? "Mot de passe incorrect." : "Impossible de supprimer le compte. Veuillez réessayer.");
    } finally {
      setDeleting(false);
    }
  };

  const startEdit = () => {
    setCoords({ address: user?.address || "", postal_code: user?.postal_code || "", phone: user?.phone || "", city: user?.city || "" });
    setEditing(true);
  };

  const saveCoords = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/account/profile", coords);
      setUser(data);
      setEditing(false);
      toast.success(`Coordonnées enregistrées. Préfecture de rattachement : ${data.departement?.prefecture}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'enregistrer vos coordonnées.");
    } finally {
      setSaving(false);
    }
  };

  const changePlan = async (action) => {
    setBusy(true);
    if (action === "upgrade") {
      try {
        const { data } = await api.post("/payments/checkout", {
          lookup_key: "premium_monthly",
          origin_url: window.location.origin,
        });
        window.location.href = data.checkout_url;
        return;
      } catch {
        toast.error("Impossible d'ouvrir la page de paiement. Veuillez réessayer.");
        setBusy(false);
        return;
      }
    }
    try {
      const { data } = await api.post("/account/cancel");
      setUser(data);
      toast.success("Votre abonnement Premium a été résilié.");
    } catch {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="page-parametres">
      <div>
        <h1 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Paramètres</h1>
        <p className="mt-1 text-sm text-slate-500">Votre profil et votre abonnement.</p>
      </div>

      <motion.section
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}
        className="card-integra p-6" data-testid="settings-profile-card"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">Mon profil</h2>
          <button onClick={startEdit} className="flex items-center gap-1.5 text-xs font-semibold text-bleu hover:underline" data-testid="profile-edit-button">
            <Pencil className="h-3 w-3" /> {user?.address ? "Modifier mes coordonnées" : "Compléter mes coordonnées"}
          </button>
        </div>
        <dl className="mt-4 space-y-3">
          {[
            { icon: UserRound, label: "Nom complet", value: user?.name },
            { icon: Mail, label: "Email", value: user?.email },
            { icon: Target, label: "Objectif", value: user?.objective || "Non renseigné" },
            { icon: Phone, label: "Téléphone", value: user?.phone || "À compléter" },
            { icon: MapPin, label: "Adresse", value: user?.address ? `${user.address}, ${user.postal_code}${user.city ? ` ${user.city}` : ""}` : "À compléter" },
            { icon: Landmark, label: "Ville", value: user?.city || "À compléter" },
          ].map((row) => (
            <div key={row.label} className="flex items-center gap-3.5 rounded-xl border border-slate-100 p-3.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-bleu-light text-bleu"><row.icon className="h-4 w-4" /></span>
              <div>
                <dt className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{row.label}</dt>
                <dd className="text-sm font-semibold text-slate-900">{row.value}</dd>
              </div>
            </div>
          ))}
        </dl>

        {user?.departement ? (
          <div className="mt-4 rounded-xl border border-bleu/15 bg-bleu-light p-4" data-testid="profile-prefecture-card">
            <p className="flex items-center gap-2 text-xs font-bold text-bleu"><Landmark className="h-4 w-4" /> Votre préfecture de rattachement</p>
            <p className="mt-1.5 text-sm font-bold text-slate-900">{user.departement.name} ({user.departement.code}) — Préfecture : {user.departement.prefecture}</p>
            <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
              Selon votre commune, une sous-préfecture peut être compétente pour vos démarches : vérifiez sur le site de votre préfecture.
              Votre adresse sert aussi à pré-remplir vos modèles de courrier administratifs.
            </p>
          </div>
        ) : (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="profile-prefecture-missing">
            <p className="text-xs font-bold text-amber-700">Complétez votre adresse et votre code postal</p>
            <p className="mt-1 text-[11px] leading-relaxed text-amber-700/90">
              Elles nous permettent de vous orienter vers la préfecture ou la sous-préfecture compétente pour votre département
              et de pré-remplir vos modèles de courrier administratifs.
            </p>
          </div>
        )}

        {editing && (
          <form onSubmit={saveCoords} className="mt-4 space-y-3 rounded-xl border border-slate-100 bg-slate-50 p-4" data-testid="profile-edit-form">
            <input value={coords.address} onChange={(e) => setCoords((c) => ({ ...c, address: e.target.value }))} required minLength={5}
              placeholder="Adresse (n° et rue)" className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-bleu focus:outline-none" data-testid="profile-address-input" />
            <div className="grid grid-cols-2 gap-3">
              <input value={coords.postal_code} onChange={(e) => setCoords((c) => ({ ...c, postal_code: e.target.value }))} required pattern="\d{5}" maxLength={5} inputMode="numeric"
                placeholder="Code postal" className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-bleu focus:outline-none" data-testid="profile-postal-input" />
              <input value={coords.phone} onChange={(e) => setCoords((c) => ({ ...c, phone: e.target.value }))} required type="tel"
                placeholder="Téléphone" className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-bleu focus:outline-none" data-testid="profile-phone-input" />
            </div>
            {cities.length > 0 && (
              <select required value={coords.city} onChange={(e) => setCoords((c) => ({ ...c, city: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 focus:border-bleu focus:outline-none"
                data-testid="profile-city-select">
                <option value="" disabled>Choisissez votre ville</option>
                {cities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            <div className="flex gap-2">
              <button type="submit" disabled={saving} className="btn-primary flex-1 !py-2.5 disabled:opacity-50" data-testid="profile-save-button">
                {saving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Enregistrer"}
              </button>
              <button type="button" onClick={() => setEditing(false)} className="rounded-full border border-slate-200 px-5 text-xs font-semibold text-slate-500" data-testid="profile-cancel-edit">Annuler</button>
            </div>
          </form>
        )}
      </motion.section>

      <motion.section
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.06, ease: EASE }}
        className="card-integra p-6" data-testid="settings-language-section"
      >
        <h2 className="flex items-center gap-2 text-base font-bold text-slate-900"><Languages className="h-4 w-4 text-bleu" /> Langue de traduction préférée</h2>
        <p className="mt-2 text-xs leading-relaxed text-slate-500">
          Cette langue est utilisée par défaut par l'Interprète IA et par le bouton « Traduire » des réponses du Coach civique et de l'Assistant démarches.
          Vous pouvez toujours la changer ponctuellement au moment de traduire.
        </p>
        <select value={user?.preferred_lang || "en"} onChange={(e) => saveLang(e.target.value)}
          className="mt-4 rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-700 focus:border-bleu focus:outline-none"
          data-testid="preferred-lang-select">
          {TARGETS.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
        </select>
      </motion.section>

      <motion.section
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.07, ease: EASE }}
        className="card-integra p-6" data-testid="settings-titre-section"
      >
        <h2 className="flex items-center gap-2 text-base font-bold text-slate-900"><CreditCard className="h-4 w-4 text-bleu" /> Mon titre de séjour</h2>
        <p className="mt-2 text-xs leading-relaxed text-slate-500">
          Renseigné automatiquement lorsque vous scannez votre titre avec l'Interprète IA, ou manuellement ci-dessous.
          Un rappel de renouvellement est ajouté à vos échéances 2 mois avant l'expiration (la demande se dépose entre 4 et 2 mois avant la fin de validité).
        </p>
        {expiryDays !== null && (
          <p className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold ${
            expiryDays < 0 ? "bg-rose-100 text-rose-700" : expiryDays <= 60 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
          }`} data-testid="titre-expiry-badge">
            <CalendarClock className="h-3.5 w-3.5" />
            {expiryDays < 0 ? `Titre expiré depuis ${-expiryDays} jour(s)` : `Expire dans ${expiryDays} jour(s)`}
          </p>
        )}
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Type de titre</label>
            <input value={titre.type} onChange={(e) => setTitre((t) => ({ ...t, type: e.target.value }))} maxLength={120}
              placeholder="Ex : Carte de séjour pluriannuelle"
              className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-bleu focus:outline-none" data-testid="titre-type-input" />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Date d'expiration</label>
            <input type="date" value={titre.expiry} onChange={(e) => setTitre((t) => ({ ...t, expiry: e.target.value }))}
              className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-bleu focus:outline-none" data-testid="titre-expiry-input" />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button onClick={saveTitre} disabled={savingTitre || titre.type.trim().length < 2}
            className="btn-primary !px-6 !py-2.5 disabled:opacity-50" data-testid="titre-save-button">
            {savingTitre ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Enregistrer mon titre"}
          </button>
          {user?.titre_type && <RenewalChecklistButton className="!bg-white !text-bleu border border-bleu/30 hover:!bg-bleu-light" />}
          <Link to="/dashboard/renouvellement" className="text-xs font-semibold text-bleu hover:underline" data-testid="titre-guide-link">
            Guide ANEF : renouveler en ligne pas à pas →
          </Link>
        </div>
      </motion.section>

      <motion.section
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.075, ease: EASE }}
        className="card-integra p-6" data-testid="settings-referral-section"
      >
        <h2 className="flex items-center gap-2 text-base font-bold text-slate-900"><Gift className="h-4 w-4 text-bleu" /> Parrainage</h2>
        <p className="mt-2 text-xs leading-relaxed text-slate-500">
          Invitez un proche à rejoindre INTEGRA FRANCE : partagez votre lien personnel, il sera accueilli
          dès son inscription.
        </p>
        {referral ? (
          <>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <input readOnly value={referral.link} onFocus={(e) => e.target.select()}
                className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs text-slate-600 focus:outline-none"
                data-testid="referral-link-input" />
              <button onClick={copyReferral}
                className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2.5 text-xs font-semibold text-slate-600 transition-colors hover:border-bleu/40 hover:text-bleu"
                data-testid="referral-copy-button">
                <Copy className="h-3.5 w-3.5" /> Copier
              </button>
              <button onClick={shareReferral} className="btn-primary !px-5 !py-2.5 !text-xs" data-testid="referral-share-button">
                <Share2 className="h-3.5 w-3.5" /> Partager
              </button>
            </div>
            <p className="mt-3 text-xs font-semibold text-emerald-700" data-testid="referral-count">
              {referral.invited_count > 0
                ? `${referral.invited_count} proche${referral.invited_count > 1 ? "s" : ""} inscrit${referral.invited_count > 1 ? "s" : ""} grâce à vous. Merci !`
                : "Personne n'a encore utilisé votre lien — lancez-vous !"}
            </p>
            <p className="mt-1 flex items-center gap-1.5 text-xs font-bold text-bleu" data-testid="referral-bonus-info">
              <Crown className="h-3.5 w-3.5 text-ambre" /> {referral.bonus_days || 7} jours Premium offerts pour chaque proche inscrit avec votre lien.
            </p>
            {referral.premium_until && (
              <p className="mt-1 text-xs font-semibold text-emerald-700" data-testid="referral-premium-until">
                Premium offert actif jusqu'au {new Date(referral.premium_until).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}.
              </p>
            )}
          </>
        ) : (
          <div className="mt-4 flex justify-center py-3"><LoaderCircle className="h-4 w-4 animate-spin text-slate-300" /></div>
        )}
      </motion.section>

      <motion.section
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.08, ease: EASE }}
        data-testid="settings-subscription-section"
      >
        <h2 className="mb-3 text-base font-bold text-slate-900">Mon abonnement</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className={`card-integra p-6 ${!premium ? "ring-2 ring-bleu/20" : ""}`} data-testid="plan-card-free">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-slate-900">Freemium</p>
              {!premium && <span className="rounded-full bg-bleu-light px-2.5 py-1 text-[10px] font-bold text-bleu">Votre plan actuel</span>}
            </div>
            <p className="mt-2 font-display text-3xl font-extrabold text-slate-900">0 €<span className="text-sm font-semibold text-slate-400">/mois</span></p>
            <ul className="mt-4 space-y-2">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-xs text-slate-600">
                  <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" /> {f}
                </li>
              ))}
            </ul>
            {premium && (
              <button onClick={() => changePlan("cancel")} disabled={busy}
                className="mt-5 w-full rounded-full border border-slate-200 py-2.5 text-xs font-semibold text-slate-500 transition-colors hover:border-rouge/40 hover:text-rouge disabled:opacity-50"
                data-testid="plan-cancel-button">
                {busy ? <LoaderCircle className="mx-auto h-4 w-4 animate-spin" /> : "Revenir au plan gratuit"}
              </button>
            )}
          </div>

          <div className={`relative overflow-hidden rounded-2xl border p-6 ${premium ? "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 ring-2 ring-amber-200" : "border-slate-100 bg-bleu-night text-white shadow-[0_20px_50px_-18px_rgba(11,27,77,0.5)]"}`} data-testid="plan-card-premium">
            <div className="flex items-center justify-between">
              <p className={`flex items-center gap-2 text-sm font-bold ${premium ? "text-amber-700" : "text-white"}`}>
                <Crown className="h-4 w-4" /> Premium
              </p>
              {premium
                ? <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-bold text-amber-700">Votre plan actuel</span>
                : <span className="rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-bold text-bleu-accent">Recommandé</span>}
            </div>
            <p className={`mt-2 font-display text-3xl font-extrabold ${premium ? "text-amber-800" : "text-white"}`}>
              7,99 €<span className={`text-sm font-semibold ${premium ? "text-amber-600" : "text-white/50"}`}>/mois</span>
            </p>
            <ul className="mt-4 space-y-2">
              {PREMIUM_FEATURES.map((f) => (
                <li key={f} className={`flex items-start gap-2 text-xs ${premium ? "text-amber-800/90" : "text-white/80"}`}>
                  <Check className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${premium ? "text-amber-600" : "text-bleu-accent"}`} /> {f}
                </li>
              ))}
            </ul>
            {!premium && (
              <button onClick={() => changePlan("upgrade")} disabled={busy}
                className="mt-5 w-full rounded-full bg-white py-3 text-xs font-bold text-bleu transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50"
                data-testid="plan-upgrade-button">
                {busy ? <LoaderCircle className="mx-auto h-4 w-4 animate-spin" /> : "Passer à Premium — 7,99 €/mois"}
              </button>
            )}
            <p className={`mt-3 text-center text-[10px] ${premium ? "text-amber-600/80" : "text-white/40"}`}>
              Paiement sécurisé par Stripe · Sans engagement · Annulable à tout moment
            </p>
          </div>
        </div>
      </motion.section>

      <PasswordChangeSection />

      <motion.section
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.12, ease: EASE }}
        className="card-integra p-6" data-testid="settings-export-section"
      >
        <h2 className="flex items-center gap-2 text-base font-bold text-slate-900"><Download className="h-4 w-4 text-bleu" /> Mes données (RGPD)</h2>
        <p className="mt-2 text-xs leading-relaxed text-slate-500">
          Conformément au droit à la portabilité (article 20 du RGPD), téléchargez en un clic une copie complète de vos données :
          profil, parcours, documents, échéances, conversations IA, courriers, résultats de quiz et suggestions (format JSON).
        </p>
        <button onClick={exportData} className="btn-secondary mt-4 !px-6 !py-2.5 !text-xs" data-testid="export-data-button">
          <Download className="h-3.5 w-3.5" /> Télécharger mes données
        </button>
      </motion.section>

      <motion.section
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.16, ease: EASE }}
        className="card-integra border-rouge/20 p-6" data-testid="settings-danger-zone"
      >
        <h2 className="flex items-center gap-2 text-base font-bold text-rouge"><Trash2 className="h-4 w-4" /> Zone de danger</h2>
        <p className="mt-2 text-xs leading-relaxed text-slate-500">
          Conformément au RGPD, vous pouvez supprimer définitivement votre compte et l'ensemble de vos données
          (parcours, documents, échéances, conversations IA, courriers, résultats de quiz). Cette action est irréversible.
          {premium && " Votre abonnement Premium sera automatiquement résilié."}
        </p>
        <button onClick={() => setDeleteOpen(true)}
          className="mt-4 rounded-full border border-rouge/40 px-5 py-2.5 text-xs font-bold text-rouge transition-colors hover:bg-rouge hover:text-white"
          data-testid="delete-account-button">
          Supprimer mon compte et mes données
        </button>
      </motion.section>

      {deleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm" onClick={() => setDeleteOpen(false)} data-testid="delete-account-modal">
          <form onSubmit={deleteAccount} className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <h3 className="font-display text-lg font-extrabold text-rouge">Supprimer mon compte</h3>
              <button type="button" onClick={() => setDeleteOpen(false)} aria-label="Fermer" className="text-slate-400 hover:text-slate-700" data-testid="delete-account-close"><X className="h-5 w-5" /></button>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-slate-500">Toutes vos données seront définitivement effacées. Cette action est irréversible.</p>
            <label className="mt-4 block text-[11px] font-bold uppercase tracking-wider text-slate-400">Motif de la suppression *</label>
            <select value={delForm.reason} onChange={(e) => setDelForm((f) => ({ ...f, reason: e.target.value }))}
              className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-rouge focus:outline-none" data-testid="delete-reason-select">
              {DELETE_REASONS.map((r) => <option key={r}>{r}</option>)}
            </select>
            <textarea value={delForm.detail} onChange={(e) => setDelForm((f) => ({ ...f, detail: e.target.value }))} rows={2} maxLength={300}
              placeholder="Précisions (facultatif) — aidez-nous à améliorer le service"
              className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm placeholder:text-slate-400 focus:border-rouge focus:outline-none" data-testid="delete-detail-input" />
            <label className="mt-3 block text-[11px] font-bold uppercase tracking-wider text-slate-400">Confirmez avec votre mot de passe *</label>
            <input type="password" required value={delForm.password} onChange={(e) => setDelForm((f) => ({ ...f, password: e.target.value }))}
              placeholder="Votre mot de passe" className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-rouge focus:outline-none" data-testid="delete-password-input" />
            <button type="submit" disabled={deleting || !delForm.password}
              className="mt-5 w-full rounded-full bg-rouge py-3 text-xs font-bold text-white transition-colors hover:bg-red-700 disabled:opacity-50" data-testid="delete-account-confirm">
              {deleting ? <LoaderCircle className="mx-auto h-4 w-4 animate-spin" /> : "Supprimer définitivement mon compte"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
